import { supabaseService } from './supabaseService';

class PortalService {
  async getUserCompanies() {
    try {
      const user = await supabaseService.getCurrentUser();
      if (!user) throw new Error('No authenticated user');

      // Get user profile to check roles
      const userProfile = await supabaseService.getUserProfile(user.id);
      
      // If user is admin or executive, get all companies
      if (userProfile?.roles?.includes('Admin') || userProfile?.roles?.includes('Executive')) {
        return await supabaseService.getCompanies();
      }
      
      // Otherwise get companies the user has access to
      return await supabaseService.getCompanies(user.id);
    } catch (error) {
      console.error('Get user companies error:', error);
      throw error;
    }
  }

  async getCompanyAssets(companyId, filters = {}) {
    try {
      return await supabaseService.getAssets(companyId, filters);
    } catch (error) {
      console.error('Get company assets error:', error);
      throw error;
    }
  }

  async getCategories() {
    try {
      return await supabaseService.getCategories();
    } catch (error) {
      console.error('Get categories error:', error);
      throw error;
    }
  }

  async logAssetClick(assetId, companyId) {
    try {
      const user = await supabaseService.getCurrentUser();
      if (user) {
        await supabaseService.logAssetClick(assetId, companyId, user.id);
      }
    } catch (error) {
      console.error('Log asset click error:', error);
      // Don't throw error for logging failures
    }
  }

  async getAuditLogs(filters = {}) {
    try {
      return await supabaseService.getAuditLogs(filters);
    } catch (error) {
      console.error('Get audit logs error:', error);
      throw error;
    }
  }

  async getLinkHealth() {
    // For now, return mock data as this would require external monitoring
    await this.delay(1000);
    
    const assets = await supabaseService.getAssets();
    return assets.map(asset => ({
      assetId: asset.id,
      assetTitle: asset.title,
      url: asset.url,
      status: Math.random() > 0.8 ? 'warning' : 'healthy',
      responseTime: Math.floor(Math.random() * 1000) + 200,
      lastChecked: new Date().toISOString(),
      httpStatus: Math.random() > 0.9 ? 500 : 200
    }));
  }

  // Admin methods
  async createCompany(companyData) {
    try {
      return await supabaseService.createCompany(companyData);
    } catch (error) {
      console.error('Create company error:', error);
      throw error;
    }
  }

  async updateCompany(id, updates) {
    try {
      return await supabaseService.updateCompany(id, updates);
    } catch (error) {
      console.error('Update company error:', error);
      throw error;
    }
  }

  async deleteCompany(id) {
    try {
      return await supabaseService.deleteCompany(id);
    } catch (error) {
      console.error('Delete company error:', error);
      throw error;
    }
  }

  async createAsset(assetData) {
    try {
      return await supabaseService.createAsset(assetData);
    } catch (error) {
      console.error('Create asset error:', error);
      throw error;
    }
  }

  async updateAsset(id, updates) {
    try {
      return await supabaseService.updateAsset(id, updates);
    } catch (error) {
      console.error('Update asset error:', error);
      throw error;
    }
  }

  async deleteAsset(id) {
    try {
      return await supabaseService.deleteAsset(id);
    } catch (error) {
      console.error('Delete asset error:', error);
      throw error;
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const portalService = new PortalService();